import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    getDocs,
    orderBy,
    query,
    serverTimestamp,
    updateDoc,
    where,
    writeBatch,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import { buildDuplicatedHolidays } from '../utils/vacationHolidays';
import type { Holiday, HolidayCreate, HolidayUpdate } from '../models/holiday.types';

const COLLECTION_NAME = 'holidays';

function yearBounds(year: number): { start: string; end: string } {
    return { start: `${year}-01-01`, end: `${year}-12-31` };
}

class HolidayServiceClass {
    async list(year?: number): Promise<Holiday[]> {
        const constraints = year
            ? [
                  where('date', '>=', yearBounds(year).start),
                  where('date', '<=', yearBounds(year).end),
              ]
            : [];

        const snapshot = await getDocs(
            query(collection(db, COLLECTION_NAME), ...constraints, orderBy('date', 'asc'))
        );

        return snapshot.docs.map((snap) => this.format(snap.id, snap.data()));
    }

    async create(data: HolidayCreate): Promise<string> {
        this.assertValid(data);

        const docRef = await addDoc(collection(db, COLLECTION_NAME), {
            ...data,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        });

        return docRef.id;
    }

    async update(id: string, data: HolidayUpdate): Promise<void> {
        if (!id) {
            throw new Error('vacations/feriado-invalido: Feriado sem identificador.');
        }

        await updateDoc(doc(db, COLLECTION_NAME, id), {
            ...data,
            updatedAt: serverTimestamp(),
        });
    }

    async remove(id: string): Promise<void> {
        if (!id) {
            throw new Error('vacations/feriado-invalido: Feriado sem identificador.');
        }

        await deleteDoc(doc(db, COLLECTION_NAME, id));
    }

    async duplicateToYear(sourceYear: number, targetYear: number): Promise<number> {
        const [source, target] = await Promise.all([
            this.list(sourceYear),
            this.list(targetYear),
        ]);

        const toCreate = buildDuplicatedHolidays(
            source,
            target.map((holiday) => holiday.date),
            sourceYear,
            targetYear
        );

        if (toCreate.length === 0) return 0;

        const batch = writeBatch(db);
        toCreate.forEach((holiday) => {
            batch.set(doc(collection(db, COLLECTION_NAME)), {
                ...holiday,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });
        });
        await batch.commit();

        return toCreate.length;
    }

    private assertValid(data: HolidayCreate): void {
        if (!data.date || !/^\d{4}-\d{2}-\d{2}$/.test(data.date)) {
            throw new Error(
                'vacations/feriado-data-invalida: A data do feriado precisa estar no formato AAAA-MM-DD.'
            );
        }

        if (!data.name?.trim()) {
            throw new Error('vacations/feriado-sem-nome: Informe o nome do feriado.');
        }
    }

    private format(id: string, data: Record<string, unknown>): Holiday {
        return {
            id,
            date: String(data.date ?? ''),
            name: String(data.name ?? ''),
            scope: (data.scope ?? 'national') as Holiday['scope'],
            recurring: data.recurring !== false,
            createdAt: (data.createdAt ?? null) as Holiday['createdAt'],
            updatedAt: (data.updatedAt ?? null) as Holiday['updatedAt'],
        };
    }
}

export const holidayService = new HolidayServiceClass();
